/**
 * The works order — one buyer's ask, on paper, for whoever prices or makes it.
 *
 * It carries NO price of any kind. That is not an omission to be helpfully
 * filled in later: this sheet exists to ask what a thing costs, and printing a
 * figure on it answers its own question. `QuoteRequestSummaryItem` does carry
 * `selectionUnitPrice` and `selectionListPriceSnapshot`; neither is rendered.
 *
 * It is also the one document here with no buyer-facing twin, so the "a print is
 * exactly what the buyer sees" rule does not govern it — the buyer never sees it.
 *
 * Reads the lead detail the screen has already loaded rather than a request
 * endpoint of its own; a request is small and always arrives with its lead.
 */
import { selectionDocRef } from '@paradise/shared';
import { createFileRoute } from '@tanstack/react-router';

import {
  useProductItemNoLookup,
  useProductMeasureLookup,
  useProductThumbLookup
} from '@/components/lead-detail/use-product-covers';
import { documentSubtitle } from '@/components/print/document-date';
import { PrintDocumentPage } from '@/components/print/print-document-page';
import { useLeadQuoteQuery } from '@/components/print/use-lead-quote-query';
import { mediaUrl } from '@/lib/media';

export const Route = createFileRoute('/_app/leads/$leadId/print/request/$requestId')({
  staticData: { hideShellChrome: true },
  component: PrintRequestRoute
});

function PrintRequestRoute() {
  const { leadId, requestId } = Route.useParams();
  const { data: leadResult, isPending } = useLeadQuoteQuery(leadId);
  const photos = useProductThumbLookup();
  const measures = useProductMeasureLookup();
  const itemNos = useProductItemNoLookup();

  const lead = leadResult?.lead ?? null;
  // Requests are deletable, so a stale link is an ordinary outcome, not a bug.
  const request = lead?.quoteRequests.find(entry => entry.id === requestId) ?? null;
  const status = isPending ? 'Loading…' : request === null ? 'That request no longer exists.' : null;

  return (
    <PrintDocumentPage
      title="Works order"
      subtitle={request ? documentSubtitle('Request', selectionDocRef(request.id), request.createdAt) : 'Request'}
      recipient={lead ? { company: lead.company, contactName: lead.contactName } : null}
      leadId={leadId}
      footerRight="No prices — for costing"
      statusMessage={status}
    >
      {request && (
        <>
          <div className="mb-3.5 rounded-md border border-border/60 p-2.5 text-[12.5px]">
            <div className="text-[10px] tracking-wider text-muted-foreground uppercase">Requested by</div>
            <div className="font-medium">
              {request.requesterName} · {request.requesterCompany}
            </div>
            <div className="text-muted-foreground">
              {[request.requesterEmail, request.requesterPhone].filter(Boolean).join(' · ')}
            </div>
            {request.message ? <div className="mt-1 whitespace-pre-wrap">{request.message}</div> : null}
          </div>

          <table className="print-items-table w-full border-collapse text-[12.5px]">
            <thead>
              <tr>
                <th className="w-[52px] border-b py-1.5 pr-2" aria-hidden="true" />
                <th className="border-b py-1.5 pr-2 text-left text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                  Item
                </th>
                <th className="border-b py-1.5 pr-2 text-right text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                  Qty
                </th>
                <th className="border-b py-1.5 text-left text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                  Buyer's note
                </th>
              </tr>
            </thead>
            <tbody>
              {request.items.map(item => {
                const thumb = photos.get(item.productId) ?? null;
                const measure = measures.get(item.productId);
                const itemNo = item.itemNo ?? itemNos.get(item.productId);
                const subLine = [itemNo === undefined ? undefined : `#${String(itemNo)}`, measure]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <tr key={item.id}>
                    <td className="border-b border-border/50 py-1.5 pr-2">
                      {thumb !== null ? (
                        // Never tinted — the factory judges finish from this.
                        <img
                          src={mediaUrl('products', thumb)}
                          alt=""
                          className="print-photo-exact h-10 w-10 rounded object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="border-border/70 h-10 w-10 rounded border" aria-hidden="true" />
                      )}
                    </td>
                    <td className="border-b border-border/50 py-1.5 pr-2">
                      {item.productName}
                      {subLine ? <div className="mt-0.5 text-[11px] text-muted-foreground">{subLine}</div> : null}
                    </td>
                    <td className="border-b border-border/50 py-1.5 pr-2 text-right tabular-nums">{item.quantity}</td>
                    <td className="border-b border-border/50 py-1.5 whitespace-pre-wrap">{item.note ?? ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </PrintDocumentPage>
  );
}
